/**
 * Floating audio player for synchronized essay reading mode.
 *
 * Plays WAV segments from the Kokoro TTS manifest sequentially.
 * Notifies the parent of the active segment ID so the essay content
 * can highlight and scroll to the current paragraph or heading.
 *
 * Rendered as a floating card anchored to the right of the viewport,
 * tracking the vertical position of the active paragraph.
 * Auto-plays on mount.
 *
 * Called by:
 * - PublicEssayPage when essay.hasAudio is true and the user clicks Listen
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  CloseOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import type { AudioSegment } from '@compendium/shared';

interface AudioPlayerProps {
  slug: string;
  segments: AudioSegment[];
  /** Viewport-relative top offset (px) for the floating card. */
  targetY: number;
  onSegmentChange: (segmentId: string | null) => void;
  onClose: () => void;
}

/**
 * Floating TTS audio player card.
 *
 * Plays essay audio segments in sequence. Exposes the current segment
 * via onSegmentChange so the parent can highlight the active text.
 * Auto-plays the first segment on mount.
 */
function AudioPlayer({ slug, segments, targetY, onSegmentChange, onClose }: AudioPlayerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Use a ref so event handlers inside effects always see the latest callback
  // without needing to re-attach listeners on every render.
  const onSegmentChangeRef = useRef(onSegmentChange);
  useEffect(() => { onSegmentChangeRef.current = onSegmentChange; }, [onSegmentChange]);

  // Single Audio element for the lifetime of the component.
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    const handleEnded = () => {
      setCurrentIndex(prev => {
        const next = prev + 1;
        const seg = segments[next];
        if (next < segments.length && seg) {
          audio.src = `/api/public/essays/${slug}/audio/${seg.id}.wav`;
          audio.play().catch(() => {});
          onSegmentChangeRef.current(seg.id);
          return next;
        }
        setIsPlaying(false);
        onSegmentChangeRef.current(null);
        return prev;
      });
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    // Auto-play first segment on mount
    const firstSeg = segments[0];
    if (firstSeg) {
      audio.src = `/api/public/essays/${slug}/audio/${firstSeg.id}.wav`;
      audio.play().catch(() => {});
      onSegmentChangeRef.current(firstSeg.id);
    }

    return () => {
      audio.pause();
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audioRef.current = null;
    };
    // segments and slug are stable after mount (manifest is fetched once).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Load and play a segment by index. */
  const playAt = useCallback((index: number) => {
    const seg = segments[index];
    if (!seg || !audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.src = `/api/public/essays/${slug}/audio/${seg.id}.wav`;
    audioRef.current.play().catch(() => {});
    setCurrentIndex(index);
    onSegmentChangeRef.current(seg.id);
  }, [segments, slug]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else if (audio.src) {
      audio.play().catch(() => {});
    } else {
      playAt(currentIndex);
    }
  };

  const handleClose = () => {
    audioRef.current?.pause();
    onSegmentChangeRef.current(null);
    onClose();
  };

  const seg = segments[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === segments.length - 1;

  // Clamp so the card stays within viewport (card height ~140px)
  const clampedY = Math.max(80, Math.min(
    typeof window !== 'undefined' ? window.innerHeight - 160 : 600,
    targetY
  ));

  return (
    <div
      style={{
        position: 'fixed',
        right: '24px',
        top: `${clampedY}px`,
        width: '252px',
        background: 'rgba(8, 8, 12, 0.97)',
        border: '1px solid rgba(212, 175, 55, 0.35)',
        borderRadius: '16px',
        padding: '14px 16px 12px',
        zIndex: 1000,
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        boxShadow: [
          '0 0 0 1px rgba(212, 175, 55, 0.08)',
          '0 0 24px rgba(212, 175, 55, 0.14)',
          '0 0 60px rgba(212, 175, 55, 0.06)',
          '0 12px 48px rgba(0, 0, 0, 0.7)',
        ].join(', '),
        transition: 'top 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Header row: icon + counter + close */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <SoundOutlined style={{ color: '#d4af37', fontSize: '13px', marginRight: '7px', flexShrink: 0 }} />
        <span
          style={{
            flex: 1,
            color: '#555',
            fontSize: '0.66rem',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
          }}
        >
          {seg?.type}&nbsp;&middot;&nbsp;{currentIndex + 1}&thinsp;/&thinsp;{segments.length}
        </span>
        <Button
          icon={<CloseOutlined />}
          type="text"
          size="small"
          onClick={handleClose}
          style={{ color: '#444', padding: '0 4px', lineHeight: 1 }}
        />
      </div>

      {/* Segment text preview */}
      <div
        style={{
          color: '#b8b8b8',
          fontSize: '0.82rem',
          lineHeight: 1.55,
          fontFamily: "var(--font-serif)",
          marginBottom: '12px',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '3.8em',
        }}
      >
        {seg?.text}
      </div>

      {/* Transport controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
        <Button
          icon={<StepBackwardOutlined />}
          type="text"
          size="small"
          onClick={() => playAt(Math.max(0, currentIndex - 1))}
          disabled={isFirst}
          style={{ color: isFirst ? '#333' : '#666', fontSize: '13px' }}
        />

        <Button
          icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
          type="text"
          onClick={handlePlayPause}
          style={{
            fontSize: '32px',
            color: '#d4af37',
            width: '44px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: isPlaying ? 'drop-shadow(0 0 8px rgba(212,175,55,0.6))' : 'none',
            transition: 'filter 0.3s',
          }}
        />

        <Button
          icon={<StepForwardOutlined />}
          type="text"
          size="small"
          onClick={() => playAt(Math.min(segments.length - 1, currentIndex + 1))}
          disabled={isLast}
          style={{ color: isLast ? '#333' : '#666', fontSize: '13px' }}
        />
      </div>
    </div>
  );
}

export default AudioPlayer;
